/**
 * stuburt-signup-toast.js — shared soft-shelf card toast for newsletter signups.
 *
 * One <stuburt-signup-toast> element per page owns a small fixed card that
 * confirms a newsletter signup, then auto-dismisses (~1s). Two trigger modes:
 *
 *   1. Server-state mode — any {% form 'customer' %} carrying `.stuburt-signup-form`
 *      (footer signup, email-signup block) submits NATIVELY, never over AJAX.
 *      This store runs Shopify spam protection, whose reCAPTCHA token is attached
 *      only by Shopify's own submit flow — a hand-rolled fetch is rejected with
 *      400 "Missing CAPTCHA token" while the native submit quietly succeeds, which
 *      is exactly what flashed "something went wrong" over a signup that had in
 *      fact gone through. So the form posts natively, reloads with Shopify's own
 *      result, and the section renders a
 *      `[data-signup-toast-autoshow="success|subscribed"]` marker that this
 *      element shows on load — the same path as mode 2. Submits are intercepted
 *      ONLY in the theme editor, to preview the toast without navigating the
 *      customizer.
 *
 *   2. Auto-show mode — a server-rendered marker element anywhere in the DOM,
 *      `[data-signup-toast-autoshow="success|subscribed|error"]`, is picked up on
 *      load and shown once. Used by the email band, which stays a native form so
 *      its discount-code reveal survives — only its "already subscribed" case is
 *      routed here. The marker (which doubles as a no-JS message) is hidden after
 *      the toast fires so the "disappearing" behaviour holds when JS is on.
 *
 * Non-negotiables honoured: vanilla Custom Element (no framework), animation
 * lives in CSS (JS only toggles [open]/[data-state]), prefers-reduced-motion
 * respected in the stylesheet, full teardown in disconnectedCallback. A
 * module-level guard means that if two ever render, only the first is live.
 */

let liveToast = null; // only the first connected instance handles behaviour

const DEFAULT_HOLD_MS = 1000; // visible hold before auto-dismiss ("after 1 second")
const EXIT_MS = 280; // must match the CSS exit transition duration

// Written by stuburt-popup.js just before its own native submit. Shopify marks
// EVERY {% form 'customer' %} on the reloaded page as posted, so the footer
// section renders its success marker even when the welcome popup was the form
// the visitor actually used — and this toast would then pop over the popup's
// discount reveal. Keep the key and the window in step with stuburt-popup.js.
const POPUP_SUBMIT_STAMP_KEY = 'stuburt_popup_submitted';
const POPUP_SUBMIT_STAMP_MAX_AGE = 60000;

function popupOwnsThisPost() {
  try {
    const raw = window.sessionStorage.getItem(POPUP_SUBMIT_STAMP_KEY) || '';
    const at = parseInt(raw.slice(raw.lastIndexOf(':') + 1), 10);
    return Boolean(at) && Date.now() - at <= POPUP_SUBMIT_STAMP_MAX_AGE;
  } catch (error) {
    return false;
  }
}

class StuburtSignupToast extends HTMLElement {
  #abort = null;
  #hideTimer = null;
  #resetTimer = null;

  connectedCallback() {
    // Single live instance. A second copy (defensive — normally impossible, since
    // footer and password page never co-occur) simply stays dormant and hidden.
    if (liveToast && liveToast !== this) return;
    liveToast = this;

    this.card = this.querySelector('[data-toast-card]');
    this.textEl = this.querySelector('[data-toast-text]');
    this.hold =
      parseInt(this.getAttribute('data-duration'), 10) || DEFAULT_HOLD_MS;
    this.messages = {
      success: this.getAttribute('data-success') || 'Thank you for signing up.',
      subscribed:
        this.getAttribute('data-subscribed') || 'You’re already on the list.',
      error:
        this.getAttribute('data-error') ||
        'Something went wrong — please try again.',
    };

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // AJAX mode: catch newsletter form posts before the browser navigates.
    document.addEventListener('submit', (event) => this.#onSubmit(event), {
      signal,
    });

    // Auto-show mode: a form that reloaded natively left us a marker.
    this.#consumeMarker();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    window.clearTimeout(this.#hideTimer);
    window.clearTimeout(this.#resetTimer);
    if (liveToast === this) liveToast = null;
  }

  // Real visitors submit natively — see the header note: Shopify's spam-protection
  // token only rides the native submit, so intercepting with fetch just earns a
  // 400 while the native submit succeeds. We step in ONLY inside the theme editor,
  // where a native submit would navigate the customizer preview; there we simply
  // preview the toast and let the section's own design-mode guard skip the post.
  #onSubmit(event) {
    if (!(window.Shopify && window.Shopify.designMode)) return;
    const form =
      event.target instanceof Element
        ? event.target.closest('form.stuburt-signup-form')
        : null;
    if (!form) return;
    event.preventDefault();
    this.#show('success');
  }

  #consumeMarker() {
    // Read synchronously, before the popup clears the stamp a frame later.
    if (popupOwnsThisPost()) return;
    const marker = document.querySelector('[data-signup-toast-autoshow]');
    if (!marker) return;
    const state = marker.getAttribute('data-signup-toast-autoshow');
    if (!this.messages[state]) return;
    // The marker doubles as the no-JS message — hide it so only the timed toast
    // remains once JS has taken over.
    marker.hidden = true;
    // Defer a frame so the enter transition runs from the hidden state.
    window.requestAnimationFrame(() => {
      // Second guard, and the one that doesn't depend on script order: by this
      // frame the popup has set [submitted] if it is showing its own reveal.
      // The sessionStorage check above can miss if the popup got there first
      // and consumed the stamp; a toast over the discount code cannot.
      if (document.querySelector('stuburt-popup[submitted]')) return;
      this.#show(state);
    });
  }

  #show(state) {
    window.clearTimeout(this.#hideTimer);
    window.clearTimeout(this.#resetTimer);
    if (this.textEl) {
      this.textEl.textContent = this.messages[state] || this.messages.error;
    }
    this.setAttribute('data-state', state);
    // Force a reflow so re-showing restarts the enter animation cleanly.
    void this.offsetWidth;
    this.setAttribute('open', '');
    this.#hideTimer = window.setTimeout(() => this.#hide(), this.hold);
  }

  #hide() {
    this.removeAttribute('open'); // CSS animates the card out
    this.#resetTimer = window.setTimeout(() => {
      this.removeAttribute('data-state');
    }, EXIT_MS);
  }
}

if (!customElements.get('stuburt-signup-toast')) {
  customElements.define('stuburt-signup-toast', StuburtSignupToast);
}
